import { FilterMultiSelect, FilterMultiSelectWithAddition } from "../../multiSelect";
import { FilterDateRange } from "../../datePicker";
import { FilterDropdown } from "../../dropdowns";
import { type GenericFiltersMap } from "utils/filterUtils";
import { type ReactNode } from "react";
import classNames from "classnames";
import { FilterCheckbox } from "components/common/checkbox/FilterCheckbox";
import { FilterMultiSelectWithWindow } from "components/multiSelect/FilterMultiSelectWithWindow";
import { FilterViewTypes } from "enums/filterViewTypes";

export interface FilterBuilderProps<T extends string> {
  filterOptions: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
  filter: GenericFiltersMap<T>;
  items: FilterItemBase<T>[];
}

export interface FilterItemBase<T extends string> {
  label: string;
  propertyName: T;
  type: FilterViewTypes;

  items?: Record<string, unknown>[];
  placeholder?: string;
  className?: string;
  wrapper?: (children: ReactNode) => React.ReactElement;
  condition?: () => boolean;
  otherProps?: object;
  hidden?: boolean;
}

const getFilterItem = <T extends string>(payload: {
  item: FilterItemBase<T>;
  filterOptions: GenericFiltersMap<T>;
  filter: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
}) => {
  const renderer = (children: ReactNode) => {
    if (payload.item.condition && !payload.item.condition()) {
      return null;
    }
    const wrapper = payload.item.wrapper;
    return wrapper ? wrapper(children) : <>{children}</>;
  };

  return (() => {
    switch (payload.item.type) {
      case FilterViewTypes.DateRange: {
        return renderer(
          !payload.item.hidden && (
            <FilterDateRange
              label={payload.item.label}
              propertyName={payload.item.propertyName}
              filter={payload.filter}
              updateFilter={payload.updateFilter}
              className={classNames(payload.item.className, "filter-item")}
              horizontalPositionSecondPicker="left"
              {...payload.item.otherProps}
            />
          ),
        );
      }
      case FilterViewTypes.MultiSelect: {
        return renderer(
          !payload.item.hidden && (
            <div className={classNames(payload.item.className, "filter-item")}>
              <div className="label">{payload.item.label}</div>
              <FilterMultiSelect
                propertyName={payload.item.propertyName}
                placeholder={payload.item.placeholder}
                items={payload.item.items || payload.filterOptions[payload.item.propertyName]}
                filter={payload.filter}
                updateFilter={payload.updateFilter}
                {...payload.item.otherProps}
              />
            </div>
          ),
        );
      }
      case FilterViewTypes.MultiSelectWithAddition: {
        return renderer(
          !payload.item.hidden && (
            <div className={classNames(payload.item.className, "filter-item")}>
              <div className="label">{payload.item.label}</div>
              <FilterMultiSelectWithAddition
                propertyName={payload.item.propertyName}
                placeholder={payload.item.placeholder}
                items={payload.item.items || payload.filterOptions[payload.item.propertyName]}
                filter={payload.filter}
                updateFilter={payload.updateFilter}
                {...payload.item.otherProps}
              />
            </div>
          ),
        );
      }
      case FilterViewTypes.MultiSelectWithWindow: {
        return renderer(
          !payload.item.hidden && (
            <div className={classNames(payload.item.className, "filter-item")}>
              <div className="label">{payload.item.label}</div>
              <FilterMultiSelectWithWindow
                propertyName={payload.item.propertyName}
                placeholder={payload.item.placeholder}
                items={payload.item.items || payload.filterOptions[payload.item.propertyName]}
                filter={payload.filter}
                updateFilter={payload.updateFilter}
                {...payload.item.otherProps}
              />
            </div>
          ),
        );
      }
      case FilterViewTypes.Select: {
        return renderer(
          !payload.item.hidden && (
            <div className={classNames(payload.item.className, "filter-item")}>
              <div className="label">{payload.item.label}</div>
              <FilterDropdown
                propertyName={payload.item.propertyName}
                items={payload.item.items || payload.filterOptions[payload.item.propertyName]}
                filter={payload.filter}
                updateFilter={payload.updateFilter}
                placeholder={payload.item.placeholder}
                defaultSelected={0}
                fluid
                selection
                {...payload.item.otherProps}
              />
            </div>
          ),
        );
      }
      case FilterViewTypes.Checkbox:
        return renderer(
          !payload.item.hidden && (
            <div className={classNames(payload.item.className, "filter-item")}>
              <div className="label">{payload.item.label}</div>
              <FilterCheckbox
                propertyName={payload.item.propertyName}
                filter={payload.filter}
                updateFilter={payload.updateFilter}
                className={classNames(payload.item.className, "filter-item")}
                {...payload.item.otherProps}
              />
            </div>
          ),
        );
      default:
        return null;
    }
  })();
};

export const FilterFormBuilder = <T extends string>(props: FilterBuilderProps<T>) => {
  const { filter, filterOptions, updateFilter } = props;
  return (
    <>
      {props.items.map((item, index) => {
        return <div key={index}>{getFilterItem({ item, filterOptions, filter, updateFilter })}</div>;
      })}
    </>
  );
};
